import React from 'react';
import { useTranslation } from 'react-i18next';
import Loader from 'Core/components/Loader';
import { useGetPaymentInfo } from 'Core/hooks/platform/useGetPaymentInfo';
import useGetNextStep from '../hooks/useGetNextStep';
import ConsentPage from 'Modules/easyCredit/routes/ConsentPage';
import EasyCreditSuccess from 'Modules/easyCredit/routes/EasyCreditSuccess';
import ApplicationExpired from 'Modules/easyCredit/routes/ApplicationExpired';
import ApplicationNotFound from 'Modules/easyCredit/routes/ApplicationNotFound';
import ApplicationApproved from 'Modules/easyCredit/routes/ApplicationApproved';
import ApplicationDenied from 'Modules/easyCredit/routes/ApplicationDenied';
import ApplicationRefunded from 'Modules/easyCredit/routes/ApplicationRefunded';

const EasyCreditRedirect = () => {
  const { t } = useTranslation();

  // Fetch payment info to get customer GID and external reference ID
  const { data: paymentInfo, isLoading: isPaymentInfoLoading } = useGetPaymentInfo();

  const customerGid = paymentInfo?.data?.customer_gid || paymentInfo?.customer_gid;
  const externalReferenceId = paymentInfo?.data?.external_reference_id || paymentInfo?.external_reference_id;

  // Fetch next step data
  const { data: nextStepData, isLoading: isNextStepLoading, isError } = useGetNextStep(
    customerGid,
    externalReferenceId,
    !!(customerGid && externalReferenceId)
  );

  // Check if there's already a redirect link - only redirect for specific statuses
  React.useEffect(() => {
    if (nextStepData?.data?.application?.easycredit_redirect_link?.redirect_link) {
      const { next_action } = nextStepData.data || {};
      const shouldRedirect = 
        next_action === 'CONSENT_REQUIRED' || 
        next_action === 'OPEN' || 
        next_action === 'COMPLETE_APPLICATION' ||
        next_action === 'PRE_APPROVED_PENDING_CUST_CONFIRMATION';
      
      if (shouldRedirect) {
        const redirectUrl = nextStepData.data.application.easycredit_redirect_link.redirect_link;
        console.log('Redirecting to Easy Credit:', redirectUrl);
        window.location.replace(redirectUrl);
      }
    }
  }, [nextStepData]);

  if (isPaymentInfoLoading || isNextStepLoading) {
    return <Loader message={t('status.loading.financingApplication') || 'Loading application...'} />;
  }

  if (isError) {
    return <ApplicationNotFound />;
  }

  if (!nextStepData) {
    return <ApplicationNotFound />;
  }

  const { next_action } = nextStepData.data || {};

  // Skip rendering if there's a redirect link and it should redirect (handled by effect above)
  if (nextStepData?.data?.application?.easycredit_redirect_link?.redirect_link) {
    const shouldRedirect = 
      next_action === 'CONSENT_REQUIRED' || 
      next_action === 'OPEN' || 
      next_action === 'COMPLETE_APPLICATION' ||
      next_action === 'PRE_APPROVED_PENDING_CUST_CONFIRMATION';
    
    if (shouldRedirect) {
      return <Loader message={t('status.loading.financingApplication') || 'Loading application...'} />;
    }
  }

  // Render the appropriate component based on next_action
  switch (next_action) {
    // Statuses that require consent or redirect to EasyCredit
    case 'CONSENT_REQUIRED':
    case 'OPEN':
    case 'PRE_APPROVED_PENDING_CUST_CONFIRMATION':
      return <ConsentPage />;

    // All approved statuses show the same approved page
    case 'NO_ACTION_ALREADY_APPROVED':
      return <ApplicationApproved />;

    // Application denied
    case 'DECLINED':
      return <ApplicationDenied />;

    // Application completed successfully
    case 'COMPLETED':
      return <EasyCreditSuccess />;

    // Application expired
    case 'EXPIRED':
      return <ApplicationExpired />;

    // Refund statuses
    case 'REFUND':
    case 'PARTIAL_REFUND':
      return <ApplicationRefunded />;

    default:
      // For empty or unknown next actions, show "application not found" page
      return <ApplicationNotFound />;
  }
};

export default EasyCreditRedirect;